import { get } from '@va/http-client';
import { RequestCanceler, useCancelOnUnmount } from '@va/util/misc';
import { useCallback, useMemo } from 'react';
import useSWR, { BareFetcher, Key, SWRConfiguration, SWRResponse as SWRResponseInterface } from 'swr';

export type SWRResponse<T, D> = SWRResponseInterface<T, D>;

export type useFetchDataParams<T, D = Error> = {
  key: Key;
  config: SWRConfiguration<T, D> & { lazyLoaded?: boolean };
  mapper?: (data: any) => T | Promise<T>;
  fetchFunc?: (...args: any[]) => Promise<unknown>;
};

export function useFetchData<T, D = Error>(
  key: useFetchDataParams<T, D>['key'],
  config?: useFetchDataParams<T, D>['config'],
  mapper?: useFetchDataParams<T, D>['mapper'],
  fetchFunc?: useFetchDataParams<T, D>['fetchFunc'],
): SWRResponse<T, D> {
  const requestId = useMemo(() => RequestCanceler.generateId(), []);
  useCancelOnUnmount(requestId);

  const fetcher = useCallback<BareFetcher<T>>(
    async (args: any[]) => {
      if (fetchFunc) {
        const response = await fetchFunc(...(Array.isArray(args) ? args : [args]));
        return (mapper ? await mapper(response) : response) as Promise<T>;
      }

      const signal = RequestCanceler.onRequestStart(requestId);
      const response = await get(Array.isArray(args) ? args[0] : args, {}, { signal });
      RequestCanceler.onRequestEnd(requestId);
      return (mapper ? await mapper(response) : response) as Promise<T>;
    },
    [fetchFunc, mapper, requestId],
  );

  const onErrorFunc = useMemo(() => {
    if (config?.onError) return config.onError;

    return () => {
      RequestCanceler.onRequestEnd(requestId);
    };
  }, [config?.onError, requestId]);

  const swr = useSWR<T, D>(key, fetcher, {
    ...config,
    onError: onErrorFunc,
  });

  return {
    ...swr,
    error: getSwrError(swr),
  };
}

export const USER_ABORTED_REQUEST_MSG = 'The user aborted a request.';
const SIGNAL_ABORTED_NO_REASON = 'signal is aborted without reason';

const isReqAbortedError = (error: Error | string) => {
  let msg = '';

  if (typeof error === 'string') {
    msg = error;
  } else if (typeof error === 'object') {
    msg = error?.message;
  }

  return (
    msg.includes(USER_ABORTED_REQUEST_MSG) ||
    // fix for requests being cancelled
    msg.includes(SIGNAL_ABORTED_NO_REASON)
  );
};

export const getSwrError = (swr: SWRResponseInterface) => {
  return isReqAbortedError(swr.error as Error) ? undefined : swr.error;
};
